/**
 * <ma-search-modal>
 * Predictive search modal for Mar Amoli.
 * - Opens via [data-ma-search-open] buttons or `ma:search:open` event
 * - Closes via overlay click, [data-ma-drawer-close], Escape, or `ma:search:close`
 * - Debounced input (200ms) to /search/suggest.json
 * - Results rendered with safe DOM construction (textContent only, never inner HTML)
 */
/* ══════════════════════════════════════════════════════════════════════════
   Lógica compartida del predictive (window.MaSuggest)

   La usan DOS superficies: este modal (la lupa del header) y el campo de la
   página /search (assets/section-ma-search.js). El render vive en un solo
   lugar a propósito: si cambia una tarjeta, cambia en las dos.

   Construcción segura del DOM: createElement + textContent, NUNCA innerHTML.
   ══════════════════════════════════════════════════════════════════════════ */

const MIN_LARGO = 2;
const TIPOS = 'product,collection,page,article';

/* Orden comercial: primero lo que se vende, al final lo informativo. */
const SECCIONES = [
  { key: 'products', heading: 'Productos', limite: 5 },
  { key: 'collections', heading: 'Colecciones', limite: 3 },
  { key: 'articles', heading: 'Del Blog', limite: 3 },
  { key: 'pages', heading: 'Páginas', limite: 3 },
];

/**
 * Precio de suggest.json. OJO: viene como string decimal EN PESOS ("159.00"),
 * no en centavos como el resto del theme — no reutilizar formatMoney(cents).
 */
function precio(valor) {
  const n = parseFloat(valor);
  if (!isFinite(n)) return '';
  try {
    return n.toLocaleString('es-MX', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'MXN',
      maximumFractionDigits: n % 1 === 0 ? 0 : 2,
    });
  } catch (e) {
    return '$' + n.toFixed(0) + ' MXN';
  }
}

/** Texto plano a partir del body HTML. summary_html viene vacío en esta tienda. */
function extracto(html, max = 90) {
  if (!html) return '';
  let texto = '';
  try {
    texto = new DOMParser().parseFromString(String(html), 'text/html').body.textContent || '';
  } catch (e) {
    texto = String(html).replace(/<[^>]*>/g, ' ');
  }
  texto = texto.replace(/\s+/g, ' ').trim();
  if (texto.length <= max) return texto;
  const corte = texto.slice(0, max);
  const espacio = corte.lastIndexOf(' ');
  return (espacio > 40 ? corte.slice(0, espacio) : corte) + '…';
}

function fecha(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  try {
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) {
    return '';
  }
}

/**
 * suggest.json entrega la imagen ORIGINAL (hasta 3400px de ancho) y la
 * pintamos a 56px. Le pedimos el resize al CDN a mano — igual que en
 * ma-category-tiles.liquid, ver docs/LEARNINGS.md.
 */
function imagen(item, ancho = 112) {
  const url = item.image || item.featured_image?.url || null;
  if (!url) return null;
  if (!/^https?:|^\/\//.test(url)) return url;
  return url + (url.includes('?') ? '&' : '?') + 'width=' + ancho;
}

/** <span class="..."> con texto seguro. Devuelve null si no hay qué pintar. */
function span(clase, texto, tag = 'span') {
  if (!texto) return null;
  const el = document.createElement(tag);
  el.className = clase;
  el.textContent = texto;
  return el;
}

/** Línea de precio de un producto: "desde $X", precio anterior tachado y Agotado. */
function precioProducto(item) {
  if (item.price == null) return null;
  const cont = document.createElement('span');
  cont.className = 'ma-search-result-price';

  const varia = item.price_min && item.price_max && item.price_min !== item.price_max;
  const actual = precio(varia ? item.price_min : item.price);
  if (!actual) return null;
  cont.appendChild(document.createTextNode(varia ? `desde ${actual}` : actual));

  const antes = parseFloat(item.compare_at_price_max);
  if (isFinite(antes) && antes > parseFloat(item.price)) {
    const s = span('ma-search-result-compare', precio(antes), 's');
    if (s) cont.appendChild(s);
  }
  if (item.available === false) {
    const badge = span('ma-search-result-badge', 'Agotado');
    if (badge) cont.appendChild(badge);
  }
  return cont;
}

/** Pide sugerencias. Devuelve los resultados o null si la consulta no aplica/falló. */
async function buscar(query) {
  if (!query || query.length < MIN_LARGO) return null;
  try {
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=${TIPOS}&resources[limit]=5`;
    const res = await fetch(url);
    const data = await res.json();
    return data.resources?.results || {};
  } catch (err) {
    console.error('Search failed', err);
    return null;
  }
}

/** Pinta las secciones dentro de `contenedor`. Devuelve cuántos resultados hubo. */
function pintar(contenedor, results) {
  if (!contenedor) return 0;
  contenedor.replaceChildren();
  let total = 0;

  SECCIONES.forEach((sec) => {
    const items = ((results && results[sec.key]) || []).slice(0, sec.limite);
    if (!items.length) return;
    total += items.length;

    const sectionEl = document.createElement('section');
    sectionEl.dataset.maSearchType = sec.key;

    const h3 = document.createElement('h3');
    h3.textContent = sec.heading;
    sectionEl.appendChild(h3);

    const ul = document.createElement('ul');
    items.forEach((item) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = item.url;

      const src = imagen(item);
      if (src) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        a.appendChild(img);
      }

      const cuerpo = document.createElement('span');
      cuerpo.className = 'ma-search-result-body';

      const titulo = span('ma-search-result-title', item.title || '');
      if (titulo) cuerpo.appendChild(titulo);

      if (sec.key === 'products') {
        const tipo = span('ma-search-result-meta', item.type);
        if (tipo) cuerpo.appendChild(tipo);
        const p = precioProducto(item);
        if (p) cuerpo.appendChild(p);
      } else if (sec.key === 'collections') {
        cuerpo.appendChild(span('ma-search-result-meta', 'Ver la colección →'));
      } else if (sec.key === 'articles') {
        const ex = span('ma-search-result-excerpt', extracto(item.summary_html || item.body));
        if (ex) cuerpo.appendChild(ex);
        const f = span('ma-search-result-meta', fecha(item.published_at));
        if (f) cuerpo.appendChild(f);
      } else if (sec.key === 'pages') {
        const ex = span('ma-search-result-excerpt', extracto(item.body));
        if (ex) cuerpo.appendChild(ex);
      }

      a.appendChild(cuerpo);
      li.appendChild(a);
      ul.appendChild(li);
    });
    sectionEl.appendChild(ul);
    contenedor.appendChild(sectionEl);
  });

  if (!total) {
    const p = document.createElement('p');
    p.className = 'ma-search-empty';
    p.textContent = 'No encontramos resultados';
    contenedor.appendChild(p);
  }
  return total;
}

const MaSuggest = { buscar, pintar, MIN_LARGO, TIPOS };
window.MaSuggest = MaSuggest;

class MaSearchModal extends HTMLElement {
  connectedCallback() {
    this._debounce = null;

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-ma-search-open]')) {
        e.preventDefault();
        this.open();
      }
    });

    this.querySelectorAll('[data-ma-drawer-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    document.addEventListener('ma:search:open', () => this.open());
    document.addEventListener('ma:search:close', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) this.close();
      if (e.key === '/' && !this.hasAttribute('open') &&
          document.activeElement.tagName !== 'INPUT' &&
          document.activeElement.tagName !== 'TEXTAREA') {
        e.preventDefault();
        this.open();
      }
    });

    const input = this.querySelector('[data-ma-search-input]');
    if (input) {
      input.addEventListener('input', () => this._onInput(input.value));
    }
  }

  open() {
    this.removeAttribute('hidden');
    this.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';
    const input = this.querySelector('[data-ma-search-input]');
    if (input) {
      requestAnimationFrame(() => input.focus());
    }
  }

  close() {
    this.removeAttribute('open');
    document.documentElement.style.overflow = '';
    setTimeout(() => {
      if (!this.hasAttribute('open')) this.setAttribute('hidden', '');
    }, 300);
  }

  _onInput(query) {
    clearTimeout(this._debounce);
    this._debounce = setTimeout(() => this._search(query), 200);
  }

  async _search(query) {
    const resultsEl = this.querySelector('[data-ma-search-results]');
    if (!query || query.length < MIN_LARGO) {
      resultsEl.replaceChildren();
      return;
    }
    const results = await MaSuggest.buscar(query);
    if (results) MaSuggest.pintar(resultsEl, results);
  }

  /** Delegado para no duplicar el render entre el modal y la página /search. */
  _render(results) {
    MaSuggest.pintar(this.querySelector('[data-ma-search-results]'), results);
  }
}

customElements.define('ma-search-modal', MaSearchModal);

window.MaSearch = {
  open: () => document.dispatchEvent(new CustomEvent('ma:search:open')),
  close: () => document.dispatchEvent(new CustomEvent('ma:search:close')),
};
